/**
 * What is behind you: nothing, a blur, or a picture of your own.
 *
 * A therapy session is held wherever the two people happen to be — a spare
 * room, a kitchen, a shared flat. Being seen is the point; having your home
 * catalogued is not, and for a patient that is often the reason they hesitate
 * to turn the camera on at all.
 *
 * Blur answers that worry and says nothing else, which is why it is still the
 * first thing offered and the one a patient can reach without uploading
 * anything. A picture is the therapist's own decision, from their own shelf —
 * never a gallery of stock scenes, which is how a call starts looking like an
 * advert for somewhere the therapist is not.
 *
 * Nothing here reaches a server as video. The segmentation runs frame by frame
 * in the browser, and what is published is the processed picture; the only
 * thing that travels is the still image, downloaded once from our own API.
 */

/**
 * How far the background is pushed out of focus.
 *
 * Enough that a room is unreadable, not so much that the edge of a head turns
 * into a halo — the artefact that makes a blurred call look broken rather
 * than private.
 */
export const BLUR_RADIUS = 12;

/**
 * Where MediaPipe's runtime is served from.
 *
 * Ours, not a CDN. The library defaults to jsdelivr for the WebAssembly and a
 * Google bucket for the model, which would have a patient's browser
 * announcing itself to two third parties at the moment they join a session.
 * `scripts/copy-mediapipe.mjs` puts both files under `public/` at build time.
 */
export const MEDIAPIPE_ASSET_PATHS = {
    tasksVisionFileSet: "/mediapipe/wasm",
    modelAssetPath: "/mediapipe/selfie_segmenter.tflite",
} as const;

/**
 * Where the choice is remembered.
 *
 * Per browser, never sent anywhere. A therapist holds six sessions a day from
 * the same desk and should not have to blur their background for each one; a
 * patient on a shared machine gets the same treatment as any other site
 * setting.
 */
export const BACKGROUND_STORAGE_KEY = "alchely.meeting.background";

/**
 * The choice, as the one word it is remembered by.
 *
 * A picture is `image:<id>` rather than a second stored key, so there is one
 * value to read, one to write, and no way for the two halves to disagree after
 * a browser wrote one of them and not the other. `"blur"` and `"none"` are
 * exactly the words the earlier version stored, so a therapist who had already
 * chosen a blur keeps it.
 */
export type BackgroundChoice = "none" | "blur" | `image:${string}`;

const IMAGE_PREFIX = "image:";

export function imageChoice(id: string): BackgroundChoice {
    return `${IMAGE_PREFIX}${id}`;
}

/** The picture's id, or null for a choice that is not a picture. */
export function backgroundImageId(choice: BackgroundChoice): string | null {
    return choice.startsWith(IMAGE_PREFIX)
        ? choice.slice(IMAGE_PREFIX.length) || null
        : null;
}

export function isBackgroundChoice(value: unknown): value is BackgroundChoice {
    if (value === "blur" || value === "none") return true;
    return (
        typeof value === "string" &&
        value.startsWith(IMAGE_PREFIX) &&
        value.length > IMAGE_PREFIX.length
    );
}

/**
 * What a picture may be, and what a file actually is.
 *
 * Three formats, matching the API: a browser can decode all three into a
 * bitmap cheaply enough to composite them at camera rate. The extension is
 * consulted only when the browser offers no type at all — dragging a file in
 * from a file manager sometimes yields an empty `type`, and refusing a
 * perfectly ordinary `.jpg` on that basis would be our bug, not the file's.
 */
export const BACKGROUND_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/** What the file picker offers, in the order a browser reads it. */
export const BACKGROUND_IMAGE_ACCEPT = `${BACKGROUND_IMAGE_TYPES.join(",")},.jpg,.jpeg,.png,.webp`;

const EXTENSION_TYPES: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
};

export function backgroundContentType(file: File): string {
    // A type with parameters on it is not one the API has an extension for.
    const reported = file.type.split(";")[0].trim().toLowerCase();
    if ((BACKGROUND_IMAGE_TYPES as readonly string[]).includes(reported)) return reported;
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return EXTENSION_TYPES[extension] ?? reported;
}

/**
 * The largest picture we will send, mirroring the API's ceiling.
 *
 * Checked here as well so a therapist who picked a 30 MB photograph is told
 * before they wait for it to upload, not after the server weighs it and throws
 * it away.
 */
export const MAX_BACKGROUND_IMAGE_BYTES = 8 * 1024 * 1024;

/** How many of their own a therapist may keep, mirroring the API's limit. */
export const MAX_OWN_BACKGROUNDS = 10;

/** How many a centre may keep, mirroring the API's limit. */
export const MAX_CENTER_BACKGROUNDS = 20;

/** One picture on a therapist's — or a centre's — shelf. */
export type MeetingBackground = {
    id: string;
    name: string;
    created_at?: string | null;
    /** On the caller's centre shelf rather than their own. */
    in_center_library: boolean;
    /** Whether the caller may remove it. */
    can_manage: boolean;
};
